import searchtabData from "@/public/data/searchTab.json";
import { Button } from "@/shared/components/common/button";
import Flex from "@/shared/components/common/Flex";
import Input from "@/shared/components/common/input/Input";
import Typography from "@/shared/components/common/Typography";
import { useDeviceType } from "@/shared/context/DeviceContext";
import { cn } from "@/shared/libs/commonUtils";
import { City } from "@phosphor-icons/react/dist/ssr";
import type React from "react";
import { type ElementType, useState } from "react";
import LocationType from "./components/LocationType";
import CitySearch from "./components/CitySearch";
import AirportSearch from "./components/AirportSearch";
import BodySkeleton from "./components/BodySkeleton";

type TLocation = "domestic" | "international";
export type TCity = {
  id: number;
  name: string;
  iata: string;
  lang_name: string;
  fa_name?: string;
};

interface LocationPickerProps {
  className?: string;
  OnCloseComp?: ElementType;
  formName?: "flight" | "hotel" | "tour" | "train" | "bus";
  onClick: (value: {
    name: string;
    id: string | number;
    type: string | undefined;
    code: string;
  }) => void;
  route?: "origin" | "destination";
  searchResult: {
    data: TCity[] | undefined;
    isLoading: boolean;
    search: string;
    setSearch: React.ChangeEventHandler<HTMLInputElement> | undefined;
  };
}

export default function LocationPicker({
  className,
  formName = "flight",
  onClick,
  OnCloseComp,
  route,
  searchResult: { data, isLoading, search, setSearch },
}: LocationPickerProps) {
  const locationData = searchtabData.tabs[formName].locationPicker;

  const {
    variants,
    search: searchPlaceholder,
    locations,
    noResult,
    title,
  } = locationData;

  const [locationType, setLocationType] = useState<TLocation>("domestic");

  return (
    <Flex className={cn("p-0 flex-col", className)}>
      {/* Title & Type */}
      <Flex className="gap-x-2 py-4 px-4">
        {route && (
          <Typography className="font-medium">
            {typeof title !== "string" ? title[route] : title}
          </Typography>
        )}
        <Typography className="font-semibold">
          {variants.filter((vari) => vari.variant === locationType)[0].title}
        </Typography>
      </Flex>

      {/* Swtich type */}
      <LocationType
        variants={variants}
        locationType={locationType}
        onSelect={(location) => setLocationType(location)}
      />

      {/* Search input */}
      <Flex className="w-full px-4 py-3">
        <Input
          value={search || ""}
          placeholder={searchPlaceholder}
          onChange={(e) => {
            if (setSearch) {
              setSearch(e);
              // Force re-render or ensure data is being fetched
            }
          }}
          autoFocus={false}
          className="rounded-xs"
        />
      </Flex>

      {isLoading ? (
        <BodySkeleton />
      ) : (
        <Flex className="w-full flex-col max-h-[400px] overflow-y-auto">
          {search && search.length > 0 ? (
            data ? (
              data.map((item, index) =>
                formName === "hotel" || formName === "tour" ? (
                  <CitySearch
                    key={`CitySearch-${index}`}
                    formName={formName}
                    city={item}
                    onClick={onClick}
                  />
                ) : (
                  <AirportSearch
                    key={`AirportSearch-${index}`}
                    airports={
                      item as AirportCity & { type: "city" | "airport" }
                    }
                    onClick={onClick}
                  />
                ),
              )
            ) : (
              <Flex className="flex-col w-full h-full border-b py-1 px-2  flex gap-3 items-center">
                <Flex className="w-full justify-center items-center gap-x-2">
                  <Typography className="font-medium">{noResult}</Typography>
                </Flex>
              </Flex>
            )
          ) : (
            locations[locationType].map(({ name, lang_name, id, iata }, z) => (
              <Flex
                className="flex-col w-full h-full border-b py-1 px-2 flex gap-x-3 items-center"
                key={`${lang_name}-${id}`}
              >
                <Flex className="w-full flex-col ">
                  <Flex
                    className="w-full justify-start items-center gap-x-2 cursor-pointer hover:bg-muted dark:hover:bg-muted/10 transition-all duration-150 py-2 rounded-xs px-1"
                    onClick={() =>
                      onClick({
                        name: lang_name,
                        id,
                        type: "city",
                        code: formName === "tour" ? name : iata,
                      })
                    }
                  >
                    <City className="text-primary" />
                    <Typography className="font-medium">{lang_name}</Typography>
                  </Flex>
                </Flex>
              </Flex>
            ))
          )}
        </Flex>
      )}

      {useDeviceType() === "mobile" && OnCloseComp && (
        <OnCloseComp className="w-full p-2 flex justify-end border-t">
          <Button className="rounded-xs text-xs  " variant="ghost">
            تایید
          </Button>
        </OnCloseComp>
      )}
    </Flex>
  );
}
